import {useCallback} from 'react';
import {useMutation as useMutationBase, useQuery as useQueryBase, useQueryClient} from 'react-query';
import {useAuth} from '../../modules/auth/providers/AuthProvider';
import {useApiUrl} from '../providers/ConfigProvider';
import {HttpHeaders, addAuthHeader, buildHttpError, deleteHttp, getHttp, isHttpError, postHttp} from './ApiClient';
import {useCallWithRefreshedToken} from './TokenRefresher';
import {trimChar} from '../helpers';

type QueryOptions = {
	enabled?: boolean;
};

type ResultBase = {
	isLoading: boolean;
	error: unknown;
};

type UseQuery = <T>(
	path: string,
	headers?: HttpHeaders,
	options?: QueryOptions,
) => {
	data: T | undefined;
	refetch: () => void;
} & ResultBase;

const useQuery: UseQuery = <T>(path: string, headers: HttpHeaders = {}) => {
	const url = useApiUrl(path);
	const query = useQueryBase(trimChar(path, '/'), () => getHttp<T>(url, headers));
	return {
		data: query.data,
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

const useAuthedQuery: UseQuery = <T>(path: string, headers: HttpHeaders = {}, options: QueryOptions = {}) => {
	const {currentUser} = useAuth();
	const url = useApiUrl(path);
	const callWithRefreshedToken = useCallWithRefreshedToken<T>();
	const query = useQueryBase(
		trimChar(path, '/'),
		async () => {
			return callWithRefreshedToken(
				() => getHttp<T>(url, addAuthHeader(headers, currentUser?.access_token)),
				(newToken: string) => {
					return getHttp<T>(url, addAuthHeader(headers, newToken));
				},
			);
		},
		options,
	);

	return {
		data: query.data,
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

interface MutationRequest {
	data?: object;
	headers?: HttpHeaders;
}

type UseMutation = <T>(path: string) => {
	mutate(req: MutationRequest): Promise<T>;
} & ResultBase;

const useMutation: UseMutation = <T>(path: string) => {
	const url = useApiUrl(path);
	const mutation = useMutationBase((req: MutationRequest) => {
		return postHttp<T>(url, req.data ?? {}, req.headers ?? {});
	});
	return {
		mutate: async (req: MutationRequest) => {
			try {
				return await mutation.mutateAsync(req);
			} catch (e) {
				if (isHttpError(e)) throw buildHttpError(e);
				throw e;
			}
		},
		isLoading: mutation.isLoading,
		error: mutation.error,
	};
};

const useAuthedMutation: UseMutation = <T>(path: string) => {
	const {currentUser} = useAuth();
	const mutation = useMutation<T>(path);
	const callWithRefreshedToken = useCallWithRefreshedToken<T>();
	return {
		...mutation,

		mutate: async (req: MutationRequest) => {
			const addTokenToReq = (token: string | undefined) => {
				return {data: req.data, headers: addAuthHeader(req.headers, token)};
			};

			return callWithRefreshedToken(
				() => mutation.mutate(addTokenToReq(currentUser?.access_token)),
				(newToken: string) => {
					return mutation.mutate(addTokenToReq(newToken));
				},
			);
		},
	};
};

interface DeletionRequest {
	headers?: HttpHeaders;
}

type UseDeletion = <T>(path: string) => {
	doDelete(req: DeletionRequest): Promise<T>;
} & ResultBase;

const useDeletion: UseDeletion = <T>(path: string) => {
	const url = useApiUrl(path);
	const mutation = useMutationBase((req: DeletionRequest) => {
		return deleteHttp<T>(url, req.headers ?? {});
	});
	return {
		doDelete: (req: DeletionRequest) => mutation.mutateAsync(req),
		isLoading: mutation.isLoading,
		error: mutation.error,
	};
};

const useAuthedDeletion: UseDeletion = <T>(path: string) => {
	const {currentUser} = useAuth();
	const mutation = useDeletion<T>(path);
	const callWithRefreshedToken = useCallWithRefreshedToken<T>();
	return {
		...mutation,
		doDelete: async (req: DeletionRequest) => {
			const addTokenToReq = (token: string | undefined) => {
				return {headers: addAuthHeader(req.headers, token)};
			};

			return callWithRefreshedToken(
				() => mutation.doDelete(addTokenToReq(currentUser?.access_token)),
				(newToken: string) => {
					return mutation.doDelete(addTokenToReq(newToken));
				},
			);
		},
	};
};

const useInvalidateQuery = (queryKey: string) => {
	const client = useQueryClient();

	const invalidate = useCallback(() => {
		client.invalidateQueries({queryKey: [queryKey]});
	}, [queryKey, client]);

	return invalidate;
};

export {useQuery, useMutation, useAuthedQuery, useAuthedMutation, useAuthedDeletion, useInvalidateQuery};
